import React, { FormEvent } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'

import { Box, FormControl, FormLabel, Grid } from '@material-ui/core'

import { ImportProductsProps } from './ImportScreen'
import { DefaultRadioGroup } from './DefaultRadioGroup'

import { useImportEvents } from '../hooks/importHooks'
import { useRadioGroupStyles } from '../hooks/radioGroupStylesHook'

import { ServerTableData } from '../store/serverTableData'

import { EnumConstraint } from '../common/tsUtils'

import { StoreAccountModel, StoreAccountProps } from '../types/accountProps'

import { StoreProductModel } from '../server/mpsklad_core/Models/StoreProductModel'
import { ImportStocksOptionsModel } from '../server/mpsklad_core/Models/ImportModels/ImportStocksModels/ImportStocksOptionsModel'

export type ImportStocksFormProps<
  TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never>,
  TRow extends StoreProductModel,
  TColumnId extends string,
  TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown}> =
  StoreAccountProps
  & ImportProductsProps<StoreAccountModel, TProducts, TRow, TColumnId, TFilterEnum>
  & {
  importStocksFunc: (accountId: number, productOptionsModel: ImportStocksOptionsModel) => Promise<void>
}

export const ImportStocksForm =
  observer(
    <TProducts extends ServerTableData<TRow, TColumnId, TFilterEnum, never>,
      TRow extends StoreProductModel,
      TColumnId extends string,
      TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown}>
    ({account, importStocksFunc, products}: ImportStocksFormProps<TProducts, TRow, TColumnId, TFilterEnum>) => {
      const classes = useStyles()
      const radioGroupClasses = useRadioGroupStyles()

      const {onStockImport, isSubmitting} = useImportEvents(account, products)

      const stockImportOptions =
        useLocalObservable<ImportStocksOptionsModel>(
          () => ({
            sumStocks: false,
            productIds: []
          }) satisfies ImportStocksOptionsModel)

      const onSubmit =
        async (e: FormEvent) => {
          e.preventDefault()
          await onStockImport(importStocksFunc, stockImportOptions)()
        }

      const radioOptions = [
        {
          value: false,
          label: 'Перезаписать остатки МоегоСклада на остатки маркетплейса'
        },
        {
          value: true,
          label: 'Прибавить остатки маректплейса к остаткам МоегоСклада'
        }
      ]

      const getRadioButtonClasses =
        (checked: boolean) => clsx(radioGroupClasses.radioButton, checked ? radioGroupClasses.selectedRadioButton : '')

      return (
        <Box className={classes.container}>
          <form onSubmit={onSubmit}>
            <Grid container className={clsx(classes.settingsBlock, 'default-border')} direction={'column'}>
              <FormLabel className={classes.headerLabel}>
                Импорт остатков в МойСклад
              </FormLabel>

              <Grid item container direction="row">
                <Grid item xs={6}>
                  <FormControl component="fieldset">
                    <Box>
                      <FormLabel component="legend" className={classes.label}>
                        <span className={classes.dash}>—</span>
                        Что делать с остатками?
                      </FormLabel>
                    </Box>

                    <DefaultRadioGroup
                      name="sumStocks"
                      options={radioOptions}
                      value={stockImportOptions.sumStocks}
                      onChange={(value: boolean) => stockImportOptions.sumStocks = value}
                      getOptionClassName={getRadioButtonClasses}
                      getOptionLabelClassName={() => radioGroupClasses.radioLabel}
                    />

                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <button
              type="submit"
              className={clsx(classes.printButton, 'default-button')}
              disabled={isSubmitting}>
              <p>
                {isSubmitting ? 'Импортируем...' : 'Импортировать остатки'}
              </p>
            </button>
          </form>
        </Box>
      )
    })

const useStyles = makeStyles(
  theme => ({
    container: {
      padding: '16px 0px 0px 0px',
      width: 'calc(100vw - 350px)'
    },
    settingsBlock: {
      marginBottom: 20,
      marginLeft: 0,
      padding: 30,
      borderRadius: 12
    },
    settingsBlockLabel: {
      color: '#1F364D',
      fontSize: 12,
      fontWeight: 600,
      marginBottom: 15,
      marginLeft: 12
    },
    printButton: {
      width: 221,
      height: 40,
      color: '#FFFFFF',
      alignItems: 'center'
    },
    radioGroup: {
      marginTop: theme.spacing(1)
    },
    dash: {
      color: '#3987CF',
      marginRight: 3
    },
    label: {
      fontSize: 12,
      '&.Mui-focused': {
        color: 'rgba(0, 0, 0, 0.54) !important'
      }
    },
    headerLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: '#1F364D',
      marginBottom: 20
    }
  }))